// Helpers for deriving the LLM-facing CRM read tools of an agent's CRM
// mapping config (the "AI Tools" tab of the HubSpot/Salesforce modals).
//
// Skills reference these tools by their fixed LLM names; availability is
// derived from agent.tools.crm the same way the backend does it, so the
// Skills tab only offers tools that will actually be declared.

export type CrmAiToolKind = 'contact' | 'account' | 'deals' | 'activity';

export const CRM_AI_TOOL_KINDS: CrmAiToolKind[] = ['contact', 'account', 'deals', 'activity'];

// MUST stay in sync with
// backend/api/services/crm_read_service.py::CRM_TOOL_LLM_NAMES.
export const CRM_TOOL_LLM_NAMES: Record<string, CrmAiToolKind> = {
  get_crm_contact: 'contact',
  get_crm_account: 'account',
  get_crm_open_deals: 'deals',
  get_crm_recent_activity: 'activity',
};

const CRM_PROVIDERS = new Set(['hubspot', 'salesforce']);
const CONTACT_OBJECTS = new Set(['contact', 'lead']);
const ACCOUNT_OBJECTS = new Set(['company', 'account']);

// Engagement types the activity tool can read. Activity is HubSpot-only in
// v1 and explicit opt-in (never seeded from legacy config).
export const CRM_ACTIVITY_TYPES = ['notes', 'meetings', 'calls'] as const;
export const CRM_ACTIVITY_PROVIDERS = new Set(['hubspot']);

/**
 * Engagement types the activity tool may read, in canonical order. An
 * explicit list (even empty) is authoritative; absent defaults to all.
 * MUST stay in sync with
 * backend/api/services/crm_read_service.py::_normalized_activity_types.
 */
export function crmNormalizedActivityTypes(crm: any): string[] {
  const raw = crm?.activity_types;
  if (!Array.isArray(raw)) return [...CRM_ACTIVITY_TYPES];
  const selected = new Set(
    raw.filter((t: any) => typeof t === 'string').map((t: string) => t.trim().toLowerCase())
  );
  return CRM_ACTIVITY_TYPES.filter(t => selected.has(t));
}

function normalizedReadFieldObjects(crm: any): string[] {
  const readFields: any[] = Array.isArray(crm?.read_fields) ? crm.read_fields : [];
  return readFields
    .filter(f => f && typeof f === 'object' && f.property && f.key)
    .map(f => String(f.object || 'contact').trim().toLowerCase());
}

/**
 * Enabled tool kinds for one CRM config entry. An explicit ai_tools list
 * (even empty) is authoritative; legacy entries seed from read_fields /
 * read_deals so pre-existing "AI Context" mappings keep working.
 *
 * MUST stay in sync with
 * backend/api/services/crm_read_service.py::CrmReadService.enabled_ai_tools.
 */
export function crmEnabledAiTools(crm: any): Set<CrmAiToolKind> {
  if (!crm || typeof crm !== 'object' || !CRM_PROVIDERS.has(crm.name)) {
    return new Set();
  }
  const kinds = new Set<CrmAiToolKind>();
  const objects = normalizedReadFieldObjects(crm);
  if (Array.isArray(crm.ai_tools)) {
    for (const kind of crm.ai_tools) {
      const normalized = String(kind).trim().toLowerCase();
      if ((CRM_AI_TOOL_KINDS as string[]).includes(normalized)) {
        kinds.add(normalized as CrmAiToolKind);
      }
    }
  } else {
    for (const object of objects) {
      if (CONTACT_OBJECTS.has(object)) kinds.add('contact');
      else if (ACCOUNT_OBJECTS.has(object)) kinds.add('account');
    }
    if (crm.read_deals) kinds.add('deals');
  }
  // The contact tool has nothing to return without mapped contact fields.
  if (kinds.has('contact') && !objects.some(o => CONTACT_OBJECTS.has(o))) {
    kinds.delete('contact');
  }
  // Activity is HubSpot-only and inert when every engagement type is off.
  if (
    kinds.has('activity') &&
    (!CRM_ACTIVITY_PROVIDERS.has(crm.name) || crmNormalizedActivityTypes(crm).length === 0)
  ) {
    kinds.delete('activity');
  }
  return kinds;
}

export interface CrmSkillToolOption {
  name: string;
  label: string;
  description: string;
}

const CRM_TOOL_OPTION_META: Record<CrmAiToolKind, Omit<CrmSkillToolOption, 'name'>> = {
  contact: {
    label: 'CRM · Contact record',
    description:
      "Read the verified visitor's own CRM contact record (mapped fields only).",
  },
  account: {
    label: 'CRM · Account & owner',
    description:
      "Read the verified visitor's CRM company/account record, including their account owner.",
  },
  deals: {
    label: 'CRM · Open deals',
    description:
      "Read open deals/opportunities on the verified visitor's account (internal context only).",
  },
  activity: {
    label: 'CRM · Recent activity',
    description:
      "Read the team's recent notes, meetings, and calls with the verified visitor (internal context; the AI may paraphrase who/when/topic, never quote).",
  },
};

/**
 * Enumerate the CRM read tools enabled across an agent's CRM configs,
 * deduped across providers. Empty when no CRM mapping enables any tool.
 * These tools only ever activate for identity-verified visitors.
 */
export function crmSkillToolOptions(actions: Record<string, any>): CrmSkillToolOption[] {
  const crms: any[] = Array.isArray(actions?.crm) ? actions.crm : [];
  const kinds = new Set<CrmAiToolKind>();
  for (const crm of crms) {
    crmEnabledAiTools(crm).forEach(kind => kinds.add(kind));
  }
  return Object.entries(CRM_TOOL_LLM_NAMES)
    .filter(([, kind]) => kinds.has(kind))
    .map(([name, kind]) => ({ name, ...CRM_TOOL_OPTION_META[kind] }));
}
